import { useEffect, useRef } from 'react'

interface SEOProps {
  title: string
  description: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string
  canonical?: string
}

// Preconnect to Google Fonts to reduce connection latency
let preconnectSet = false
function setupPreconnect() {
  if (preconnectSet) return
  preconnectSet = true
  const domains = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com']
  domains.forEach((href) => {
    if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = href
      if (href.includes('gstatic')) link.crossOrigin = 'anonymous'
      document.head.insertBefore(link, document.head.firstChild)
    }
  })
}
setupPreconnect()

function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name'
  let el = document.querySelector(
    `meta[${attr}="${name}"]`,
  ) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.content = content
}

let faviconSet = false

function setFavicon() {
  if (faviconSet) return
  faviconSet = true

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
    <rect width="64" height="64" rx="14" fill="%230f766e"/>
    <text x="32" y="40" font-family="Arial,sans-serif" font-weight="800" font-size="16" fill="white" text-anchor="middle" letter-spacing="1">ESCA</text>
  </svg>`

  let link = document.querySelector(
    'link[rel="icon"]',
  ) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.type = 'image/svg+xml'
  link.href = `data:image/svg+xml,${svg}`
}

function applySEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  canonical,
}: SEOProps) {
  // Favicon
  setFavicon()

  // Title — set immediately
  document.title = title

  // Basic meta
  setMetaTag('description', description)
  if (keywords) {
    setMetaTag('keywords', keywords)
  }

  // Open Graph
  setMetaTag('og:title', ogTitle || title, true)
  setMetaTag('og:description', ogDescription || description, true)
  setMetaTag('og:type', ogType, true)
  setMetaTag('og:site_name', 'ESCA — Executive Search Consulting Agency', true)
  if (ogImage) {
    setMetaTag('og:image', ogImage, true)
  }
  if (canonical) {
    setMetaTag('og:url', canonical, true)
    let canonicalLink = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null
    if (!canonicalLink) {
      canonicalLink = document.createElement('link')
      canonicalLink.rel = 'canonical'
      document.head.appendChild(canonicalLink)
    }
    canonicalLink.href = canonical
  }

  // Twitter Card
  setMetaTag('twitter:card', 'summary_large_image')
  setMetaTag('twitter:title', ogTitle || title)
  setMetaTag('twitter:description', ogDescription || description)
  if (ogImage) {
    setMetaTag('twitter:image', ogImage)
  }

  // Language
  setMetaTag('content-language', 'uk')
  document.documentElement.lang = 'uk'
}

// Set default SEO immediately on module load (before any component renders)
// This ensures crawlers see correct meta even if they only parse initial JS
applySEO({
  title:
    'ESCA — Executive Search & Recruiting Agency | Підбір керівників та спеціалістів',
  description:
    'ESCA — міжнародна агенція з підбору керівників та спеціалістів. 20+ років досвіду, база 20 000+ кандидатів. Executive Search, IT Recruitment, комплектація команд.',
  keywords:
    'executive search, підбір персоналу, рекрутинг, HR агенція, підбір керівників, IT рекрутинг, хедхантинг, Україна, ESCA',
})

export function useSEO(props: SEOProps) {
  // Apply synchronously on first render (not waiting for useEffect)
  const isFirstRender = useRef(true)
  if (isFirstRender.current) {
    isFirstRender.current = false
    applySEO(props)
  }

  // Also apply on subsequent updates (language change, navigation)
  useEffect(() => {
    applySEO(props)
  }, [
    props.title,
    props.description,
    props.keywords,
    props.ogTitle,
    props.ogDescription,
    props.ogImage,
    props.ogType,
    props.canonical,
  ])
}
